import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { MapPin, Mail, Building2, CheckCircle2 } from 'lucide-react';
import { mockContactSubmission } from '../mock';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    organisation: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await mockContactSubmission(formData);
      setSubmitSuccess(true);
      setFormData({ name: '', organisation: '', email: '', message: '' });
      
      // Reset success message after 5 seconds
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Contact Us</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Partner with us to power a cleaner future
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Get in Touch</h3>
              <p className="text-gray-600 leading-relaxed mb-8">
                Whether you're a government body, financial institution, or potential partner, we'd love to hear from you. Let's discuss how we can collaborate on India's clean energy journey.
              </p>
            </div>

            {/* Address Card */}
            <Card className="border-emerald-100 bg-emerald-50">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-700 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-emerald-900 mb-2">Registered Office</CardTitle>
                    <CardDescription className="text-emerald-800">
                      <div className="space-y-1">
                        <p className="font-medium">TenSolar Energy India Pvt. Ltd.</p>
                        <p>WeWork, Berger Tower</p>
                        <p>Noida, Uttar Pradesh, India</p>
                      </div>
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Info Items */}
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-white rounded-lg border border-gray-100">
                <MapPin className="w-6 h-6 text-emerald-700 flex-shrink-0" />
                <div>
                  <div className="font-medium text-gray-900">Primary Operation</div>
                  <div className="text-gray-600 text-sm">Madhya Pradesh, India</div>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-white rounded-lg border border-gray-100">
                <Mail className="w-6 h-6 text-emerald-700 flex-shrink-0" />
                <div>
                  <div className="font-medium text-gray-900">Business Inquiries</div>
                  <div className="text-gray-600 text-sm">Use the contact form</div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <Card className="border-gray-200 shadow-lg">
            <CardHeader>
              <CardTitle>Send us a message</CardTitle>
              <CardDescription>
                Fill out the form below and we'll get back to you soon
              </CardDescription>
            </CardHeader>
            <CardContent>
              {submitSuccess ? (
                <div className="py-12 text-center">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-emerald-700" />
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">
                    Thank you for your interest!
                  </h4>
                  <p className="text-gray-600">
                    We will get back to you soon.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Name *
                    </label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="organisation" className="block text-sm font-medium text-gray-700 mb-2">
                      Organisation *
                    </label>
                    <Input
                      id="organisation"
                      name="organisation"
                      value={formData.organisation}
                      onChange={handleChange}
                      required
                      placeholder="Your organisation"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your.email@example.com"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Tell us about your inquiry..."
                      rows={5}
                      className="w-full"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-emerald-700 hover:bg-emerald-800 text-white py-6 text-base font-medium"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;
